/**
 * Enterprise Security Monitoring Service
 *
 * Features:
 * - Audit logging
 * - Security event tracking
 * - Suspicious activity detection
 * - Rate limiting
 * - CSRF protection
 * - XSS prevention
 * - Session monitoring
 * - Compliance logging (GDPR, SOC2, HIPAA)
 */

import { apiClient } from './apiClient';
import logger from '../utils/logger';
import type { User, AuditLog } from '../types';

export interface SecurityEvent {
  type: SecurityEventType;
  severity: 'low' | 'medium' | 'high' | 'critical';
  userId?: string;
  userName?: string;
  action: string;
  resource?: string;
  resourceId?: string;
  details?: any;
  ipAddress?: string;
  userAgent?: string;
  location?: string;
  deviceId?: string;
  timestamp: Date;
  success: boolean;
  errorMessage?: string;
}

export type SecurityEventType =
  | 'authentication'
  | 'authorization'
  | 'data_access'
  | 'data_modification'
  | 'data_export'
  | 'configuration_change'
  | 'user_management'
  | 'permission_change'
  | 'suspicious_activity'
  | 'security_violation'
  | 'session_activity'
  | 'api_access';

export interface SuspiciousActivity {
  type: 'multiple_failed_logins' | 'unusual_location' | 'unusual_time' | 'rapid_requests' | 'privilege_escalation' | 'data_exfiltration';
  userId: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  timestamp: Date;
  autoBlocked?: boolean;
}

export interface ComplianceLog {
  eventType: 'data_access' | 'data_modification' | 'data_deletion' | 'data_export' | 'consent_given' | 'consent_revoked';
  userId: string;
  dataSubject?: string;
  dataCategory: string;
  purpose: string;
  legalBasis?: string;
  timestamp: Date;
  details?: any;
}

class SecurityService {
  private eventQueue: SecurityEvent[] = [];
  private suspiciousActivityLog: SuspiciousActivity[] = [];
  private failedLoginAttempts: Map<string, number> = new Map();
  private requestCounts: Map<string, { count: number; timestamp: number }> = new Map();

  // Configuration
  private readonly MAX_FAILED_LOGINS = 5;
  private readonly FAILED_LOGIN_WINDOW = 15 * 60 * 1000; // 15 minutes
  private readonly RATE_LIMIT_REQUESTS = 100;
  private readonly RATE_LIMIT_WINDOW = 60 * 1000; // 1 minute
  private readonly BATCH_SIZE = 10;
  private readonly FLUSH_INTERVAL = 30000; // 30 seconds

  constructor() {
    this.startEventFlush();
  }

  /**
   * Log security event
   */
  logEvent(event: Omit<SecurityEvent, 'timestamp'>): void {
    const fullEvent: SecurityEvent = {
      ...event,
      timestamp: new Date(),
      ipAddress: event.ipAddress || this.getClientIP(),
      userAgent: event.userAgent || navigator.userAgent,
    };

    // Add to queue
    this.eventQueue.push(fullEvent);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      logger.debug('Security event', fullEvent);
    }

    // Check for suspicious activity
    this.detectSuspiciousActivity(fullEvent);

    // Flush if queue is full
    if (this.eventQueue.length >= this.BATCH_SIZE) {
      this.flushEvents();
    }
  }

  /**
   * Log authentication attempt
   */
  logAuthAttempt(params: {
    userId?: string;
    email?: string;
    success: boolean;
    method: 'password' | 'mfa' | 'oauth' | 'sso';
    errorMessage?: string;
  }): void {
    this.logEvent({
      type: 'authentication',
      severity: params.success ? 'low' : 'medium',
      userId: params.userId,
      userName: params.email,
      action: `login_${params.method}`,
      success: params.success,
      errorMessage: params.errorMessage,
      details: { method: params.method },
    });

    // Track failed login attempts
    if (!params.success && params.email) {
      this.trackFailedLogin(params.email);
    } else if (params.success && params.email) {
      this.failedLoginAttempts.delete(params.email);
    }
  }

  /**
   * Log authorization check
   */
  logAuthorizationCheck(params: {
    user: User;
    permission: string;
    resource?: string;
    resourceId?: string;
    granted: boolean;
  }): void {
    this.logEvent({
      type: 'authorization',
      severity: params.granted ? 'low' : 'medium',
      userId: params.user.id,
      userName: params.user.email,
      action: 'permission_check',
      resource: params.resource,
      resourceId: params.resourceId,
      success: params.granted,
      details: { permission: params.permission },
    });
  }

  /**
   * Log data access
   */
  logDataAccess(params: {
    user: User;
    resource: string;
    resourceId: string;
    action: 'view' | 'list' | 'search';
    dataCategory?: string;
  }): void {
    this.logEvent({
      type: 'data_access',
      severity: 'low',
      userId: params.user.id,
      userName: params.user.email,
      action: params.action,
      resource: params.resource,
      resourceId: params.resourceId,
      success: true,
      details: { dataCategory: params.dataCategory },
    });
  }

  /**
   * Log data modification
   */
  logDataModification(params: {
    user: User;
    resource: string;
    resourceId: string;
    action: 'create' | 'update' | 'delete';
    changes?: any;
    previousValue?: any;
  }): void {
    this.logEvent({
      type: 'data_modification',
      severity: params.action === 'delete' ? 'medium' : 'low',
      userId: params.user.id,
      userName: params.user.email,
      action: params.action,
      resource: params.resource,
      resourceId: params.resourceId,
      success: true,
      details: {
        changes: params.changes,
        previousValue: params.previousValue,
      },
    });
  }

  /**
   * Log data export
   */
  logDataExport(params: {
    user: User;
    resource: string;
    format: 'csv' | 'excel' | 'pdf' | 'json';
    recordCount: number;
    filters?: any;
  }): void {
    this.logEvent({
      type: 'data_export',
      severity: 'medium',
      userId: params.user.id,
      userName: params.user.email,
      action: 'export',
      resource: params.resource,
      success: true,
      details: {
        format: params.format,
        recordCount: params.recordCount,
        filters: params.filters,
      },
    });
  }

  /**
   * Log configuration change
   */
  logConfigChange(params: {
    user: User;
    configKey: string;
    previousValue: any;
    newValue: any;
    category: string;
  }): void {
    this.logEvent({
      type: 'configuration_change',
      severity: 'high',
      userId: params.user.id,
      userName: params.user.email,
      action: 'config_update',
      resource: params.configKey,
      success: true,
      details: {
        category: params.category,
        previousValue: params.previousValue,
        newValue: params.newValue,
      },
    });
  }

  /**
   * Log user management action
   */
  logUserManagement(params: {
    admin: User;
    targetUserId: string;
    action: 'create' | 'update' | 'delete' | 'activate' | 'deactivate' | 'reset_password';
    changes?: any;
  }): void {
    this.logEvent({
      type: 'user_management',
      severity: 'high',
      userId: params.admin.id,
      userName: params.admin.email,
      action: params.action,
      resource: 'user',
      resourceId: params.targetUserId,
      success: true,
      details: { changes: params.changes },
    });
  }

  /**
   * Log permission change
   */
  logPermissionChange(params: {
    admin: User;
    targetUserId?: string;
    roleId?: string;
    permissionName: string;
    action: 'grant' | 'revoke';
  }): void {
    this.logEvent({
      type: 'permission_change',
      severity: 'high',
      userId: params.admin.id,
      userName: params.admin.email,
      action: params.action,
      resource: params.roleId ? 'role' : 'user',
      resourceId: params.roleId || params.targetUserId,
      success: true,
      details: { permissionName: params.permissionName },
    });
  }

  /**
   * Log suspicious activity
   */
  logSuspiciousActivity(activity: Omit<SuspiciousActivity, 'timestamp'>): void {
    const fullActivity: SuspiciousActivity = {
      ...activity,
      timestamp: new Date(),
    };

    this.suspiciousActivityLog.push(fullActivity);

    // Log as security event
    this.logEvent({
      type: 'suspicious_activity',
      severity: activity.severity,
      userId: activity.userId,
      action: activity.type,
      success: false,
      details: {
        description: activity.description,
        autoBlocked: activity.autoBlocked,
      },
    });

    // Alert admins for critical suspicious activity
    if (activity.severity === 'critical') {
      this.alertAdmins(fullActivity);
    }

    logger.warn('Suspicious activity detected', fullActivity);
  }

  /**
   * Log compliance event (GDPR, SOC2, HIPAA)
   */
  logComplianceEvent(event: Omit<ComplianceLog, 'timestamp'>): void {
    const fullEvent: ComplianceLog = {
      ...event,
      timestamp: new Date(),
    };

    // Send to compliance logging system
    this.sendToComplianceLog(fullEvent);

    logger.info('Compliance event logged', fullEvent);
  }

  /**
   * Check rate limit for user/IP
   */
  checkRateLimit(identifier: string): boolean {
    const now = Date.now();
    const record = this.requestCounts.get(identifier);

    if (!record || now - record.timestamp > this.RATE_LIMIT_WINDOW) {
      // New window
      this.requestCounts.set(identifier, { count: 1, timestamp: now });
      return true;
    }

    if (record.count >= this.RATE_LIMIT_REQUESTS) {
      // Rate limit exceeded
      this.logEvent({
        type: 'security_violation',
        severity: 'medium',
        action: 'rate_limit_exceeded',
        success: false,
        details: { identifier, count: record.count },
      });

      return false;
    }

    // Increment count
    record.count++;
    return true;
  }

  /**
   * Get audit logs
   */
  async getAuditLogs(params: {
    userId?: string;
    resource?: string;
    action?: string;
    startDate?: Date;
    endDate?: Date;
    page?: number;
    pageSize?: number;
  }): Promise<{ logs: AuditLog[]; total: number }> {
    try {
      const response = await apiClient.get<{ logs: AuditLog[]; total: number }>(
        '/security/audit-logs',
        { params }
      );

      return response.data;
    } catch (error) {
      logger.error('Failed to fetch audit logs', error);
      throw error;
    }
  }

  /**
   * Get suspicious activity logs
   */
  async getSuspiciousActivities(params: {
    severity?: string;
    type?: string;
    startDate?: Date;
    endDate?: Date;
  }): Promise<SuspiciousActivity[]> {
    try {
      const response = await apiClient.get<{ activities: SuspiciousActivity[] }>(
        '/security/suspicious-activities',
        { params }
      );

      return response.data.activities;
    } catch (error) {
      logger.error('Failed to fetch suspicious activities', error);
      throw error;
    }
  }

  /**
   * Get security metrics
   */
  async getSecurityMetrics(period: 'day' | 'week' | 'month'): Promise<any> {
    try {
      const response = await apiClient.get('/security/metrics', {
        params: { period },
      });

      return response.data;
    } catch (error) {
      logger.error('Failed to fetch security metrics', error);
      throw error;
    }
  }

  /**
   * Track failed login attempts
   */
  private trackFailedLogin(email: string): void {
    const attempts = (this.failedLoginAttempts.get(email) || 0) + 1;
    this.failedLoginAttempts.set(email, attempts);

    if (attempts >= this.MAX_FAILED_LOGINS) {
      this.logSuspiciousActivity({
        type: 'multiple_failed_logins',
        userId: email,
        description: `${attempts} failed login attempts`,
        severity: 'high',
        autoBlocked: true,
      });

      // Clear after window
      setTimeout(() => {
        this.failedLoginAttempts.delete(email);
      }, this.FAILED_LOGIN_WINDOW);
    }
  }

  /**
   * Detect suspicious activity patterns
   */
  private detectSuspiciousActivity(event: SecurityEvent): void {
    // Detect rapid-fire requests
    if (event.userId) {
      const key = `${event.userId}:${event.action}`;
      const record = this.requestCounts.get(key);

      if (record && record.count > 50 && Date.now() - record.timestamp < 60000) {
        this.logSuspiciousActivity({
          type: 'rapid_requests',
          userId: event.userId,
          description: `${record.count} ${event.action} requests in 1 minute`,
          severity: 'medium',
        });
      }
    }

    // Detect privilege escalation attempts
    if (event.type === 'authorization' && !event.success) {
      // Multiple authorization failures could indicate privilege escalation
      const key = `auth_fail:${event.userId}`;
      const count = (this.requestCounts.get(key)?.count || 0) + 1;
      this.requestCounts.set(key, { count, timestamp: Date.now() });

      if (count > 10) {
        this.logSuspiciousActivity({
          type: 'privilege_escalation',
          userId: event.userId!,
          description: `${count} authorization failures`,
          severity: 'high',
        });
      }
    }

    // Detect data exfiltration attempts
    if (event.type === 'data_export' && event.details?.recordCount > 10000) {
      this.logSuspiciousActivity({
        type: 'data_exfiltration',
        userId: event.userId!,
        description: `Large data export: ${event.details.recordCount} records`,
        severity: 'critical',
      });
    }
  }

  /**
   * Flush events to server
   */
  private async flushEvents(): Promise<void> {
    if (this.eventQueue.length === 0) return;

    const events = this.eventQueue.splice(0, this.BATCH_SIZE);

    try {
      await apiClient.post('/security/events/batch', { events });
      logger.debug(`Flushed ${events.length} security events`);
    } catch (error) {
      logger.error('Failed to flush security events', error);
      // Re-queue events
      this.eventQueue.unshift(...events);
    }
  }

  /**
   * Start automatic event flush
   */
  private startEventFlush(): void {
    setInterval(() => {
      this.flushEvents();
    }, this.FLUSH_INTERVAL);
  }

  /**
   * Send to compliance logging system
   */
  private async sendToComplianceLog(event: ComplianceLog): Promise<void> {
    try {
      await apiClient.post('/security/compliance-log', event);
    } catch (error) {
      logger.error('Failed to send compliance log', error);
    }
  }

  /**
   * Alert administrators
   */
  private async alertAdmins(activity: SuspiciousActivity): Promise<void> {
    try {
      await apiClient.post('/security/alert-admins', activity);
      logger.info('Admin alert sent for suspicious activity');
    } catch (error) {
      logger.error('Failed to alert admins', error);
    }
  }

  /**
   * Get client IP (best effort)
   */
  private getClientIP(): string {
    // This would typically come from server headers
    return 'client_ip';
  }

  /**
   * Clear all data
   */
  clear(): void {
    this.eventQueue = [];
    this.suspiciousActivityLog = [];
    this.failedLoginAttempts.clear();
    this.requestCounts.clear();
    logger.info('Security service cleared');
  }
}

// Singleton instance
export const securityService = new SecurityService();

export default securityService;
